/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import useMediaQuery from '@mui/material/useMediaQuery';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useTranslation } from 'react-i18next';

import pageTitles from 'helpers/page-titles';
import Logo from 'assets/logo.png';
import NavMenu from 'layouts/Main/components/NavMenu';
import useTranslatedDrawerConfig from 'helpers/translateDrawerConfig';

import DrawerMenuItemWithSubMenu from './components/DrawerMenuItemWithSubMenu';
import DrawerMenuItem from './components/DrawerMenuItem';
import Notifications from './components/Notifications';
import ContactUs from './components/ContactUs';
import useGetParams from './components/ContactUs/hooks/useGetParams';

const drawerWidth = 275;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: 'hidden',
  '::-webkit-scrollbar': {
    width: '10px'
  },
  '::-webkit-scrollbar-track': {
    background: '#f1f1f1'
  },
  '::-webkit-scrollbar-thumb': {
    background: '#15243e',
    borderRadius: '10px'
  },
  '::-webkit-scrollbar-thumb:hover': {
    background: '#15243e'
  }
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`
  },
  '::-webkit-scrollbar': {
    width: '10px'
  },
  '::-webkit-scrollbar-track': {
    background: '#f1f1f1'
  },
  '::-webkit-scrollbar-thumb': {
    background: '#15243e',
    borderRadius: '10px'
  },
  '::-webkit-scrollbar-thumb:hover': {
    background: '#15243e'
  }
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open'
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  })
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open'
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme)
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme)
  })
}));

export default function Main() {
  const [open, setOpen] = useState(true);
  const [pageTitle, setPageTitle] = useState('');
  const [openMenuIndex, setOpenMenuIndex] = useState<number>();

  const translatedDrawerConfig = useTranslatedDrawerConfig();
  const { t } = useTranslation('global');

  const theme = useTheme();
  const navigate = useNavigate();
  const matches = useMediaQuery(theme.breakpoints.up('xs'));
  const matchToHideDrawer = useMediaQuery('(min-width:950px)');
  const getParams = useGetParams();

  const currentPath = useLocation().pathname;

  useEffect(() => {
    const currentTitle = pageTitles.find((item) =>
      currentPath.includes(item.path)
    );
    setPageTitle(currentTitle ? t(currentTitle.title) : '');
  }, [currentPath, t]);

  useEffect(() => {
    setOpen(matchToHideDrawer);
  }, [matchToHideDrawer]);

  const handleAddMenuIndex = useCallback((index: number) => {
    setOpenMenuIndex(index);
  }, []);

  const handleExpandDrawer = useCallback(() => {
    setOpen((prevOpenState) => !prevOpenState);
  }, []);

  const hanDleExpandDrawerFromMenus = () => {
    if (!open) setOpen(true);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed">
        <Toolbar
          sx={{
            backgroundColor: 'white',
            '&.MuiToolbar-root': { paddingX: '10px' }
          }}
        >
          <Box
            style={{ display: 'flex', alignItems: 'center' }}
            sx={{ flexGrow: 1 }}
          >
            <div
              onClick={() => navigate('/')}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginRight: '24px',
                cursor: 'pointer'
              }}
            >
              <img
                alt="Gestión de Servicios"
                loading="lazy"
                src={Logo}
                width={76}
              />
              <Typography
                color="inherit"
                sx={{ color: '#0E335C', fontSize: '10px', marginTop: '5px' }}
                variant="caption"
                noWrap
              >
                {t('general.serviceManagement')}
              </Typography>
            </div>
            {matches && (
              <Tooltip title={pageTitle}>
                <Typography
                  color="inherit"
                  fontWeight={500}
                  sx={{
                    color: '#0E335C',
                    textDecoration: 'underline',
                    fontSize: {
                      xs: '15px',
                      sm: '20px',
                      md: '24px'
                    },
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                  variant="h6"
                >
                  {pageTitle}
                </Typography>
              </Tooltip>
            )}
          </Box>

          <ContactUs googleChatLink={getParams?.data?.data?.GoogleChat?.Link} />
          <Notifications />
          <NavMenu />
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          backgroundColor: 'var(--primary)'
        }}
      >
        {/* DRAWER MENU GENERAL */}
        <Drawer anchor="left" open={open} variant="permanent">
          <DrawerHeader />
          <List
            sx={{
              backgroundColor: 'var(--primary)',
              color: 'white',
              height: '500vh',
              '@media (max-height: 450px)': {
                height: 'auto'
              }
            }}
          >
            <ListItemButton onClick={handleExpandDrawer}>
              <ListItemIcon style={{ color: '#FFFFFF' }}>
                {open ? (
                  <ChevronLeftIcon />
                ) : (
                  <Tooltip title="Menu">
                    <MenuIcon />
                  </Tooltip>
                )}
              </ListItemIcon>
              {open && (
                <ListItemText
                  primary={t('general.hideMenu')}
                  sx={{ opacity: open ? 1 : 0 }}
                />
              )}
            </ListItemButton>
            {translatedDrawerConfig.map((item, index) =>
              item.submenu ? (
                <DrawerMenuItemWithSubMenu
                  addMenuIndex={handleAddMenuIndex}
                  handleExpandDrawer={hanDleExpandDrawerFromMenus}
                  index={index}
                  isMenuOpen={openMenuIndex === index}
                  item={item}
                  key={item.key}
                  open={open}
                />
              ) : (
                <DrawerMenuItem item={item} key={item.key} open={open} />
              )
            )}
          </List>
          <Box
            sx={{
              backgroundColor: 'var(--primary)',
              height: '10vh',
              color: 'white',
              width: '100%',
              fontSize: '12px',
              display: open ? 'flex' : 'none',
              justifyContent: 'center'
            }}
          >
            <p>v6.1.5</p>
          </Box>
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          paddingTop: '88px',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '100vh',
          overflow: 'hidden',
          position: 'relative',
          '@media (max-width: 450px)': {
            padding: '68px 10px 10px',
            marginBottom: '5px'
          }
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
